import { useState } from "react";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { X } from "lucide-react";

// Real TIOB gallery, grouped by event
const ALBUMS = [
  {
    title: "Simchat Torah",
    date: "October 17, 2025",
    photos: [
      "/wp-content/uploads/2025/10/stor5.jpg",
      "/wp-content/uploads/2025/10/stor1.jpg",
      "/wp-content/uploads/2025/10/stor3-rotated.jpg",
      "/wp-content/uploads/2025/10/stor4-rotated.jpg",
      "/wp-content/uploads/2025/10/stor2.jpg",
      "/wp-content/uploads/2025/10/stor6-rotated.jpg",
      "/wp-content/uploads/2025/10/stor7.jpg",
    ],
  },
  {
    title: "Sukkot - Shake, Shake, Shake",
    date: "October 10, 2025",
    photos: [
      "/wp-content/uploads/2025/10/sukkot2-scaled.png",
      "/wp-content/uploads/2025/10/sukkot3-scaled.png",
      "/wp-content/uploads/2025/10/sukkot1-scaled.png",
    ],
  },
  {
    title: "Social Action - Food Pantry Assistance",
    date: "July 17, 2025",
    photos: [
      "/wp-content/uploads/2025/07/sa2-scaled.jpg",
      "/wp-content/uploads/2025/07/sa1-scaled.jpg",
    ],
  },
  {
    title: "Religious School Picnic",
    date: "May 17, 2025",
    photos: [
      "/wp-content/uploads/2025/05/classroom1.jpg",
      "/wp-content/uploads/2025/05/BBQ-1-scaled.jpg",
      "/wp-content/uploads/2025/05/BBQ-11.jpg",
      "/wp-content/uploads/2025/05/BBQ-6.jpg",
      "/wp-content/uploads/2025/05/BBQ-13.jpg",
      "/wp-content/uploads/2025/05/BBQ-15.jpg",
      "/wp-content/uploads/2025/05/BBQ-4.jpg",
    ],
    captions: { 1: "Snow Cones", 2: "Bounce House", 3: "Water Slide", 5: "Our chefs" },
  },
  {
    title: "Tu B'Shevat Seder",
    date: "February 14, 2025",
    photos: [
      "/wp-content/uploads/2025/02/IMG_20250219_122346-768x1024.jpg",
      "/wp-content/uploads/2025/02/20250214_194551-768x1024.jpg",
      "/wp-content/uploads/2025/02/20250214_194559-768x1024.jpg",
      "/wp-content/uploads/2025/02/20250214_194543-768x1024.jpg",
      "/wp-content/uploads/2025/02/20250214_195335-768x1024.jpg",
      "/wp-content/uploads/2025/02/20250214_194527-768x1024.jpg",
      "/wp-content/uploads/2025/02/20250214_194539-768x1024.jpg",
      "/wp-content/uploads/2025/02/20250214_195538-1-768x1024.jpg",
    ],
  },
  {
    title: "Shabbat Under the Stars",
    date: "January 31, 2025",
    photos: [
      "/wp-content/uploads/2025/02/karen1-300x225.jpg",
      "/wp-content/uploads/2025/02/karen2-300x289.jpg",
      "/wp-content/uploads/2025/02/stars5-225x300.jpeg",
      "/wp-content/uploads/2025/02/stars2-225x300.jpeg",
      "/wp-content/uploads/2025/02/stars4-225x300.jpeg",
      "/wp-content/uploads/2025/02/stars6-225x300.jpeg",
    ],
    captions: {
      0: "Waiting for the service to start",
      1: "The Siddur for the Evening",
      2: "Listening to the Rabbi",
      3: "The Rabbi leading services",
    },
  },
  {
    title: "Lil Rev",
    date: "January 16, 2025",
    photos: [
      "/wp-content/uploads/2024/12/lilrev2-232x300.png",
      "/wp-content/uploads/2025/01/lilrev2-1-225x300.jpg",
      "/wp-content/uploads/2025/01/lilrev1-225x300.jpg",
      "/wp-content/uploads/2025/01/lilrev4.jpg",
    ],
    captions: { 1: "At the Meet and Greet before the performance", 3: "Lil Rev Performing" },
  },
];

export default function Gallery() {
  const [lightbox, setLightbox] = useState(null); // { album, idx }

  const open = (albumIdx, idx) => setLightbox({ albumIdx, idx });
  const close = () => setLightbox(null);
  const move = (dir) => {
    if (!lightbox) return;
    const album = ALBUMS[lightbox.albumIdx];
    const next = (lightbox.idx + dir + album.photos.length) % album.photos.length;
    setLightbox({ ...lightbox, idx: next });
  };

  return (
    <>
      <SEOHead
        title="Photo Gallery"
        description="Photos from TIOB events - Shabbat Under the Stars, Tu B'Shevat Seder, Sukkot, Simchat Torah, Religious School Picnic, Social Action, and more."
        path="/gallery"
      />
      <PageHero
        eyebrow="Photo Gallery"
        title="Moments from our community life."
        subtitle="Shabbat under the stars, seders, religious school picnics, food-pantry days, and holidays - the year at TIOB, told in pictures."
      />

      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 space-y-20">
          {ALBUMS.map((album, ai) => (
            <article key={album.title} data-testid={`album-${ai}`}>
              <header className="flex flex-wrap items-end justify-between gap-4 mb-7 pb-4 border-b border-[var(--line)]">
                <div>
                  <p className="eyebrow mb-2">★ {album.date}</p>
                  <h2 className="font-display text-3xl md:text-4xl text-[var(--blue-ink)]">{album.title}</h2>
                </div>
                <span className="font-mono-accent text-xs text-[var(--muted)] uppercase tracking-widest">
                  {album.photos.length} photo{album.photos.length === 1 ? "" : "s"}
                </span>
              </header>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {album.photos.map((src, i) => (
                  <button
                    type="button"
                    key={src}
                    onClick={() => open(ai, i)}
                    className="group relative overflow-hidden rounded-2xl aspect-[4/3] bg-[var(--cream)] focus:outline-none focus:ring-2 focus:ring-[var(--blue)]"
                    data-testid={`gallery-photo-${ai}-${i}`}
                  >
                    <img
                      src={src}
                      alt={album.captions?.[i] || `${album.title} photo ${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    {album.captions?.[i] && (
                      <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[var(--blue-ink)]/85 via-[var(--blue-ink)]/50 to-transparent text-white text-xs p-3 pt-8 text-left font-medium">
                        {album.captions[i]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-[var(--blue-ink)]/90 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          data-testid="gallery-lightbox"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X size={26} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); move(-1); }}
            aria-label="Previous photo"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl flex items-center justify-center"
          >‹</button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); move(1); }}
            aria-label="Next photo"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl flex items-center justify-center"
          >›</button>

          <figure className="max-w-5xl max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={ALBUMS[lightbox.albumIdx].photos[lightbox.idx]}
              alt=""
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
            <figcaption className="mt-4 text-white text-center">
              <p className="font-display text-xl">{ALBUMS[lightbox.albumIdx].title}</p>
              <p className="text-white/70 text-sm">
                {ALBUMS[lightbox.albumIdx].date} - Photo {lightbox.idx + 1} of {ALBUMS[lightbox.albumIdx].photos.length}
                {ALBUMS[lightbox.albumIdx].captions?.[lightbox.idx] && ` - ${ALBUMS[lightbox.albumIdx].captions[lightbox.idx]}`}
              </p>
            </figcaption>
          </figure>
        </div>
      )}
    </>
  );
}
